import { Grid } from '@mui/material'
import { VesselVisitCargoFormStore } from '@planning/pages/VesselVisit/Stores/VesselVisitCargoFormStore'
import { useTranslate } from '@tolgee/react'
import { DataControlCard, DataControlCardProps, RequiredField } from '@tom-ui/ui'
import { observer } from 'mobx-react-lite'
import { VesselVisitViewAttribute } from '../../molecules/VesselVisitViewAttribute'
import { VesselVisitCargoSubForm } from './VesselVisitCargoSubForm'

import { Typography, useTheme } from '@mui/material'
import { CargoType, VesselType } from '@planning/app/api'

export type VesselVisitCargoViewProps = {
  store: VesselVisitCargoFormStore
}

const VesselVisitCargoViewMode = observer(({ store }: VesselVisitCargoViewProps) => {
  const { t } = useTranslate()
  const theme = useTheme()

  const { cargoType, dischargeEstimate, loadEstimate, restowEstimate } = store.data

  const estimateLabel = `${t('discharge', 'Discharge')}/${t('load', 'Load')}/${t('restow', 'Restow')}`
  const estimateValue = `${dischargeEstimate}/${loadEstimate}/${restowEstimate}`

  const hasAnyEstimate = !!(dischargeEstimate || loadEstimate || restowEstimate)
  const hasCargoType = !!cargoType

  const shouldShow = hasCargoType || hasAnyEstimate

  if (!shouldShow) {
    return (
      <Typography variant='body2' color={theme.palette.text.secondary}>
        {t('noInformationYet', 'No information yet')}
      </Typography>
    )
  }

  return (
    <>
      {hasCargoType && <VesselVisitViewAttribute label={t('cargo', 'Cargo')} value={cargoType} />}
      {hasAnyEstimate && <VesselVisitViewAttribute label={estimateLabel} value={estimateValue} />}
    </>
  )
})

type VesselVisitCargoEditModeProps = {
  store: VesselVisitCargoFormStore
  isCargoTypeLocked: boolean
  vesselType?: VesselType
}

const VesselVisitCargoEditMode = observer(
  ({ store, isCargoTypeLocked, vesselType }: VesselVisitCargoEditModeProps) => {
    return (
      <Grid container spacing={2} width={'100%'}>
        <Grid size={{ xs: 12 }}>
          <VesselVisitCargoSubForm
            store={store}
            isCargoTypeLocked={isCargoTypeLocked}
            vesselType={vesselType}
          />
        </Grid>
      </Grid>
    )
  },
)

type VesselVisitCargoControlCardProps = Partial<DataControlCardProps> & {
  vesselVisitCargoFormStore: VesselVisitCargoFormStore
  vesselType?: VesselType
  isEditMode: boolean
}

export const VesselVisitCargoControlCard = observer((props: VesselVisitCargoControlCardProps) => {
  const { t } = useTranslate()
  const { vesselVisitCargoFormStore, vesselType, isEditMode } = props

  return (
    <DataControlCard
      {...props}
      title={
        <>
          {t('cargo', 'Cargo')} <RequiredField />
        </>
      }
      allowEdit
      autoFocusId='dischargeEstimate'
      dataCy='cargo'
      onClickEdit={() => {
        if (!vesselVisitCargoFormStore.cargoType.value) {
          vesselVisitCargoFormStore.cargoType.setValue(CargoType.Container)
        }
      }}
      viewComponent={<VesselVisitCargoViewMode store={vesselVisitCargoFormStore} />}
      editComponent={
        <VesselVisitCargoEditMode
          store={vesselVisitCargoFormStore}
          isCargoTypeLocked={isEditMode}
          vesselType={vesselType}
        />
      }
      // isEditMode here referes to the edit mode of the page, not the card
      defaultEditMode={!isEditMode}
    />
  )
})
